/* import __COLOCATED_TEMPLATE__ from './sq-field-select.hbs'; */
/**
## Field Select
`sq-field-select` is a "field"-styled `<select>`. While technically the input is standalone, it's really
intended to be nested inside a Form Field block component. The two components communicate with each other. There's also
some heavier lifting to auto place the caret based on the size of the select prompt or label of the value that's selected.
(In the below examples, due to limitations in the Fuse Docs, the auto caret positioning will not work.)

```example
@title Basic Example
@description Notice the border styling looks slightly off. That's because like other Field components, they are designed to work inside a larger form-table or form-row.

@controller-properties
{
  categoryTypes: ['red', 'orange', 'yellow', 'green', 'blue', 'purple']
}

@hbs
{{#sq-form-field label="Color"}}
  {{sq-field-select content=categoryTypes
                    prompt="What is your favorite color?"}}
{{/sq-form-field}}
```

```example
@title Example with Objects

@description Here, the content of this field select is an array of objects that each have a `token` and `name` property. We also placed it in a form-table and form-row.

@controller-properties
{
  users: [{name: "Marie", token: "1"}, {name: "Liz", token:"2"}, {name: "Alex", token:"3"}],
  value: null
}


@hbs
<div class="form-table">
  <div class="form-row">
    {{#sq-form-field label="First Name"}}
      {{sq-field-select content=users
                        value=value
                        optionValuePath="token"
                        optionLabelPath="name"
                        prompt="Select Employee"
                        enablePromptSelection=true}}
    {{/sq-form-field}}
  </div>
</div>

<h4>The value of this field select is {{value}}</h4>
```
### Parameters
@param {Array} [content=[]] - Can take an array of Strings, Numbers or Objects. The items of the array will be used as both the value property and the inner text of each <option> element inside the rendered `sq-field-select`. When using objects you need to state (using `optionGroupPath`, `optionLabelPath` and `optionValuePath`) which property should be accessed on each object to supply the value attribute of the <option> and which property should be used to supply the element text.
@param {Boolean} [disabled=false] - Set to `true` to disable the field select.
@param {Boolean} [enablePromptSelection=false] - Set to `true` to enables a prompt for the select.
@param {String} [optionLabelPath="label"] - Specifies the path on each object to the desired property for the element's text.
@param {String} [optionValuePath="value"] - Used to specify the path on each object to the desired property for the `value` attribute.
@param {Boolean} [useOptionsAsValues=false] - Enable to select the entire object as `value` when selecting instead of using `optionValuePath`.
@param {String} [name=null] - The value of the HTML name attribute for this field.
@param {String} [value=null] - The selected option.
@param {Boolean} [includeBaseStyling=true] - Pass `false` to remove the base styling for this component and render an unstyled select that you can style as you wish.
@param {Function} onChange - Action to call when the selected option changes. Will receive the new selected option.

Sass dependencies at: `app/assets/stylesheets/bridgeport/components/form-elements/formfield/_formfield-select.sass`
 */

import { computed, get } from '@ember/object';
import { isArray } from '@ember/array';
import Component from '@ember/component';
import FieldElementSupport from '../utils/field-element-support';
import { guidFor } from '@ember/object/internals';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend(FieldElementSupport, {
  classNameBindings: ['includeBaseStyling:form-field__select-container', 'includeBaseStyling:form-field__caret-container'],
  content: null,
  optionValuePath: 'value',
  optionLabelPath: 'label',
  optionDisabledPath: 'disabled',
  disabled: false,
  useOptionsAsValues: false,
  enablePromptSelection: false,
  name: null,
  value: null,
  prompt: null,
  isInvalid: false,
  includeBaseStyling: true,
  selectPaddingBuffer: 0,
  selectId: computed(function () {
    return `${guidFor(this)}-select`;
  }),
  optionsAreStringsOrNumbers: computed('content.[]', function () {
    const options = this.get('content');
    return isArray(options) && options.every(option => {
      return typeof option === 'string' || typeof option === 'number';
    });
  }),
  change(e) {
    if (this.get('isDestroyed')) {
      return;
    }
    const selectIndex = e.target.selectedIndex;
    const optionsIndex = this.get('prompt') ? selectIndex - 1 : selectIndex;

    // Ember Arrays seem to sometimes not return the desired option if you
    // just use `[]`, so we use Ember Array's `objectAt` if present
    const selectedOption = typeof this.get('content').objectAt === 'function' ? this.get('content').objectAt(optionsIndex) : this.get('content')[optionsIndex];
    let selectedValue;
    if (this.get('useOptionsAsValues') || this.get('optionsAreStringsOrNumbers')) {
      selectedValue = selectedOption;
    } else {
      selectedValue = get(selectedOption, this.get('optionValuePath'));
    }
    this.set('value', selectedValue);
    if (this.get('onChange')) {
      this.get('onChange')(selectedOption);
    }
  }
});